/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useCountUp } from 'shared/hooks/use-count-up';
import { i18n } from 'shared/i18n';
import UDAnimatedNumber from 'ud-ui/components/animated-number';
import UDCard from 'ud-ui/components/card';
import UDText from 'ud-ui/components/text';
import IconBadge, { AnalyticsTone, TONE_COLORS } from './icon-badge';
import {
  KPI_CARD_CLASS,
  KPI_ICON_SIZE,
  KPI_PROGRESS_CLASS,
  KPI_PROGRESS_TRACK_CLASS,
  KPI_TOP_CLASS,
  KPI_VALUE_CLASS,
} from './kpi-layout';

interface KpiCardProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  tone?: AnalyticsTone;
  /** Доля от целого, 0..1: под значением рисуется полоса прогресса. */
  progress?: number;
  /** Пояснение к полосе, например «из 29». */
  progressHint?: string;
}

const Fill = styled.div<{ $color: string; $ratio: number }>`
  height: 100%;
  width: ${({ $ratio }) => Math.round($ratio * 100)}%;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ $color }) => $color};
`;

/**
 * Формат выбирается по итоговому значению, а не по промежуточному: у дробного итога `1.00` по дороге не
 * превращается в `1`, и ширина числа не дёргается. Целое — с разрядами по языку, дробное — с двумя знаками.
 */
function createFormatNumber(finalValue: number): (value: number) => string {
  return Number.isInteger(finalValue) ? (value) => new Intl.NumberFormat(i18n.locale).format(value) : (value) => value.toFixed(2);
}

const KpiCard = React.memo(({ icon, value, label, tone = 'gold', progress, progressHint }: KpiCardProps) => {
  const hasProgress = progress !== undefined;
  // Полоса набирается вместе с числом: та же длительность и кривая, она не «стоит готовой» под бегущей цифрой.
  const animatedProgress = useCountUp(progress ?? 0, { precision: 3 });
  const formatNumber = useMemo(() => createFormatNumber(typeof value === 'number' ? value : 0), [value]);

  return (
    <UDCard radius="panel" padding="lg" interactive className={KPI_CARD_CLASS}>
      {/* Заголовок — в левом верхнем углу карточки; под ним значок и значение. */}
      <UDText variant="body-1" as="div" className="!text-inkPrimary">
        {label}
      </UDText>
      <div className={KPI_TOP_CLASS}>
        <IconBadge icon={icon} tone={tone} size={KPI_ICON_SIZE} />
        <UDText variant="title-1" as="div" className={classNames(KPI_VALUE_CLASS, '!text-inkPrimary')}>
          {typeof value === 'string' ? value : <UDAnimatedNumber value={value} format={formatNumber} />}
        </UDText>
      </div>
      {hasProgress && (
        <div className={KPI_PROGRESS_CLASS}>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className={`${KPI_PROGRESS_TRACK_CLASS} bg-surfaceSunken overflow-hidden`}
          >
            <Fill $color={TONE_COLORS[tone]} $ratio={Math.min(Math.max(animatedProgress, 0), 1)} />
          </div>
          {progressHint && (
            <UDText variant="text-2" as="div" className="!text-inkMuted">
              {progressHint}
            </UDText>
          )}
        </div>
      )}
    </UDCard>
  );
});

export default KpiCard;
